import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randNumber, randRecentDate } from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MockServer } from './server.js'
import { createErrorResponse } from './utils.js'

export type WeightData = SchemaAttributes<'Weight'>

export const model = (Model as ModelDefinition<WeightData>).extend({
  patient: belongsTo('patient'),
  linkedConsultation: belongsTo('consultation'),
  createdUser: belongsTo('user'),
  modifiedUser: belongsTo('user'),
})

export const factory = Factory.extend<WeightData>({
  weight() {
    return randNumber({ min: 1, max: 100, fraction: 1 })
  },
  timestamp() {
    return randRecentDate().toISOString()
  },
})

export function routes(server: MockServer) {
  server.post('/weights', function (schema, request) {
    const requestBody = JSON.parse(request.requestBody)
    const attributes = requestBody.data.attributes
    const relationships = requestBody.data.relationships

    if (!relationships?.patient?.data?.id) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "patient" relationship' }],
      })
    }

    const patient = schema.find('patient', relationships.patient.data.id)
    if (!patient) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Patient not found' }],
      })
    }

    const weightData: Partial<WeightData> & {
      patientId?: string
      linkedConsultationId?: string
    } = {
      weight: attributes.weight,
      timestamp: attributes.timestamp,
      patientId: relationships.patient.data.id,
    }

    if (relationships?.linked_consultation?.data?.id) {
      weightData.linkedConsultationId =
        relationships.linked_consultation.data.id
    }

    const weight = schema.create('weight', weightData)

    // Update patient's weight and weighted_at fields
    const weightValue = attributes.weight
    const timestamp = attributes.timestamp
    // Default to "lbs" to match Patient factory format (e.g., "15.5 lbs")
    const weightUnit = 'lbs'
    const formattedWeight = `${weightValue} ${weightUnit}`

    patient.update({
      weight: formattedWeight,
      weighted_at: timestamp,
    })

    return weight
  })
}
